'use client';

import React, { useState } from 'react';
import PostList from './PostList';
import PostEditor from './PostEditor';
import PostPreview from './PostPreview';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface EditorPost {
  id: string;
  platform: string;
  format: string;
  title: string;
  content: string;
  status: string;
  tone: string;
  day: number;
  words: number;
  chars: number;
}

export default function ContentEditorLayout() {
  const [selectedPostId, setSelectedPostId] = useState<string>('');
  const [editedPosts, setEditedPosts] = useState<EditorPost[]>([]);
  const { user } = useAuth();
  const supabase = createClient();

  const updatePost = (id: string, content: string) => {
    setEditedPosts((prev) =>
      prev.map((p) =>
        p.id === id
          ? {
              ...p,
              content,
              words: content.split(/\s+/).filter(Boolean).length,
              chars: content.length,
              status: 'Bearbeitet',
            }
          : p
      )
    );
  };

  const updateStatus = (id: string, status: string) => {
    setEditedPosts((prev) =>
      prev.map((p) => (p.id === id ? { ...p, status } : p))
    );
  };

  const handleSave = async (post: EditorPost, content: string) => {
    if (!user?.id) throw new Error('Nicht angemeldet');

    const wordCount = content.split(/\s+/).filter(Boolean).length;

    const { error } = await supabase.from('reports').upsert(
      {
        user_id: user.id,
        title: post.title,
        platform: post.platform,
        format: post.format,
        tone: post.tone,
        status: post.status,
        word_count: wordCount,
        updated_at: new Date().toISOString(),
      },
      { onConflict: 'id' }
    );

    if (error) throw error;
  };

  const selectedPost = editedPosts.find((p) => p.id === selectedPostId) ?? editedPosts[0] ?? null;

  if (editedPosts.length === 0) {
    return (
      <section className="flex h-screen overflow-hidden bg-background items-center justify-center" aria-label="Content Editor">
        <div className="text-center">
          <div className="w-16 h-16 rounded-full bg-surface-2 flex items-center justify-center mx-auto mb-4">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-muted">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
              <polyline points="14 2 14 8 20 8"/>
              <line x1="12" y1="18" x2="12" y2="12"/>
              <line x1="9" y1="15" x2="15" y2="15"/>
            </svg>
          </div>
          <p className="text-sm font-500 text-white mb-1">Keine Beiträge vorhanden</p>
          <p className="text-xs text-muted">Generiere Beiträge im Content Generator, um sie hier zu bearbeiten.</p>
        </div>
      </section>
    );
  }

  return (
    <section className="flex h-screen overflow-hidden bg-background" aria-label="Content Editor">
      <PostList
        posts={editedPosts}
        selectedId={selectedPostId || (editedPosts[0]?.id ?? '')}
        onSelect={setSelectedPostId}
      />
      {selectedPost && (
        <>
          <PostEditor
            post={selectedPost}
            onUpdate={(content) => updatePost(selectedPost.id, content)}
            onStatusChange={(status) => updateStatus(selectedPost.id, status)}
            onSave={handleSave}
          />
          <PostPreview post={selectedPost} />
        </>
      )}
    </section>
  );
}